import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { qk, invalidatePracticeScoring } from "@/lib/query-keys";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfigurationFullPageSkeleton } from "@/components/ui/skeletons/ConfigurationPageSkeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLocation, useNavigate } from "react-router-dom";
import { MipsDataService } from "@/lib/mips-data-service";
import { CmsMeasure } from "@/lib/mips-data-service";
import { ConflictPreferencesCard } from "@/components/configuration/ConflictPreferences";
import { DisplayPreferencesCard } from "@/components/configuration/DisplayPreferencesCard";
import { PIConfigurationTab } from "@/components/configuration/PIConfigurationTab";
import { IAConfigurationTab } from "@/components/configuration/IAConfigurationTab";
import { CostConfigurationTab } from "@/components/configuration/CostConfigurationTab";
import { QPPReportsTab } from "@/components/configuration/QPPReportsTab";
import { ExploreMvpsTab } from "@/components/configuration/ExploreMvpsTab";
import { YearLockBanner } from "@/components/gap-management/shared/YearLockBanner";
import { FileText, AlertTriangle, Loader2, Info, AlertCircle, Search, Target, CheckCircle2, PartyPopper, BarChart3, Circle, CircleDot } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/components/ui/use-toast";
import { 
  IA_SUBCATEGORIES, 
  getSubcategoryIdFromActivityId, 
  getSubcategoryBadgeColor,
  formatActivityId,
  getSubcategoryName
} from "@/lib/ia-data-utils";
import { 
  addIASelection, 
  removeIASelection, 
  getIASelections,
  getIARequirements,
  type IASelection,
  type IARequirements
} from "@/lib/ia-calculator-utils";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { NoPracticeState } from "@/components/shared/NoPracticeState";
import { useResolvedFinancials } from "@/hooks/queries/useMvpQueries";
import userStorage from "@/lib/userStorage";

// Reweight allocation table as per requirements
const REWEIGHT_ALLOCATION = {
  'no-reweight': { 
    large: { quality: 30, cost: 30, ia: 15, pi: 25 }, 
    small: { quality: 30, cost: 30, ia: 15, pi: 25 } 
  },
  'cost-reweight': { 
    large: { quality: 55, cost: 0, ia: 15, pi: 30 }, 
    small: { quality: 55, cost: 0, ia: 15, pi: 30 } 
  },
  'pi-reweight': { 
    large: { quality: 55, cost: 30, ia: 15, pi: 0 }, 
    small: { quality: 40, cost: 30, ia: 30, pi: 0 } 
  },
  'quality-reweight': { 
    large: { quality: 0, cost: 30, ia: 15, pi: 55 }, 
    small: { quality: 0, cost: 30, ia: 15, pi: 55 } 
  },
  'ia-reweight': { 
    large: { quality: 45, cost: 30, ia: 0, pi: 25 }, 
    small: { quality: 45, cost: 30, ia: 0, pi: 25 } 
  },
  'cost-pi-reweight': { 
    large: { quality: 85, cost: 0, ia: 15, pi: 0 }, 
    small: { quality: 50, cost: 0, ia: 50, pi: 0 } 
  },
  'cost-quality-reweight': { 
    large: { quality: 0, cost: 0, ia: 15, pi: 85 }, 
    small: { quality: 0, cost: 0, ia: 15, pi: 85 } 
  },
  'cost-ia-reweight': { 
    large: { quality: 85, cost: 0, ia: 0, pi: 15 }, 
    small: { quality: 70, cost: 0, ia: 0, pi: 30 } 
  },
  'pi-quality-reweight': { 
    large: { quality: 0, cost: 50, ia: 50, pi: 0 }, 
    small: { quality: 0, cost: 50, ia: 50, pi: 0 } 
  },
  'pi-ia-reweight': { 
    large: { quality: 70, cost: 30, ia: 0, pi: 0 }, 
    small: { quality: 70, cost: 30, ia: 0, pi: 0 } 
  },
  'quality-ia-reweight': { 
    large: { quality: 0, cost: 30, ia: 0, pi: 70 }, 
    small: { quality: 0, cost: 30, ia: 0, pi: 70 } 
  }
} as const;

export default function MeasureSelection() {
  // Gate BEFORE mounting the content component (same pattern as APP Plus's
  // NoAcoState): auth resolved + no practice selected = clean empty state,
  // rendered synchronously — no skeleton flash, and no hooks-order churn in
  // the 1,400-line content component below.
  const { user, loading: authLoading, selectedPractice } = useAuth();
  if (!authLoading && user !== undefined && !selectedPractice) return <NoPracticeState />;
  return <MeasureSelectionContent />;
}

function MeasureSelectionContent() {
  console.log('[Config] Component render started');
  
  // Add mount/unmount tracking
  useEffect(() => {
    console.log('[MOUNT/UNMOUNT] Configuration component MOUNTED at', new Date().toISOString());
    return () => console.log('[MOUNT/UNMOUNT] Configuration component UNMOUNTED at', new Date().toISOString());
  }, []);
  
  // Track EVERY render to see what's causing re-renders
  useEffect(() => {
    console.log('[Config] Re-render triggered at', new Date().toISOString());
  }); // No dependency array - logs on EVERY render
  
  const location = useLocation();
  const navigate = useNavigate();
  const hash = location.hash.replace(/^#/, '');
  // Leaf tabs (deep-linkable). Primary shell groups Practice + Reporting.
  // IMPORTANT: Configuration is keep-mounted in DashboardLayout (display:none
  // when off-route), so useState(initial) alone NEVER re-reads the hash on
  // revisit — Overview "Manage MVP" deep links were landing on the stale tab.
  const CONFIG_LEAF_TABS = [
    'qpp-reports', 'practice', 'preferences', 'quality', 'pi', 'cost', 'ia', 'mvps',
  ] as const;
  type ConfigLeafTab = (typeof CONFIG_LEAF_TABS)[number];
  const isConfigLeafTab = (v: string): v is ConfigLeafTab =>
    (CONFIG_LEAF_TABS as readonly string[]).includes(v);
  const isReportingLeaf = (t: string): t is 'quality' | 'pi' | 'cost' | 'ia' =>
    t === 'quality' || t === 'pi' || t === 'cost' || t === 'ia';
  const isPracticeGroupLeaf = (t: string): t is 'practice' | 'preferences' =>
    t === 'practice' || t === 'preferences';
  // Skeleton / cold-paint tab from the URL (keep-mounted deep links).
  const hashTab: ConfigLeafTab = isConfigLeafTab(hash) ? hash : 'qpp-reports';
  
  // Get context FIRST - before ANY state initialization
  const { toast } = useToast();
  const { user, selectedPractice } = useAuth();
  const { programYear } = useProgramYear();
  const queryClient = useQueryClient();

  /** Keep Manage MVPs / assemble-inputs in sync when Profile special-status flags change. */
  const syncPracticeSettingsCache = useCallback((
    practiceId: string,
    year: number,
    patch: Record<string, unknown>,
  ) => {
    const key = qk.practiceSettings(practiceId, year);
    queryClient.setQueryData(key, (old: any) => (old ? { ...old, ...patch } : { ...patch }));
    void queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient]);
  
  console.log('[Config] Context values:', {
    userDefined: user !== undefined,
    user: user ? 'exists' : 'undefined',
    selectedPractice: selectedPractice?.id || 'none',
    practiceAlias: selectedPractice?.practice_alias || 'none',
    practiceTin: selectedPractice?.tin || 'none'
  });
  
  // Check if auth context is ready IMMEDIATELY
  if (user === undefined) {
    console.log('[Config] User undefined - showing skeleton (context loading)');
    // Context still loading - return skeleton immediately
    // NO states initialized yet!
    return (
      <>
        <Helmet>
          <title>MIPS Configuration – Quality, PI, IA, Practice Details</title>
          <meta name="description" content="Configure MIPS: select Quality/PI/IA measures and set practice details including category reweighting." />
          <link rel="canonical" href="/measure-selection" />
        </Helmet>
        <ConfigurationFullPageSkeleton activeTab={hashTab} />
      </>
    );
  }
  
  console.log('[Config] Context ready - initializing states');
  
  // NOW we know context is ready - safe to initialize states
  const selectedPracticeId = selectedPractice?.id?.toString();
  console.log('[Config] Selected practice ID:', selectedPracticeId || 'none');
  const resolvedFinancialsQ = useResolvedFinancials(selectedPracticeId, programYear);
  const partBSource = resolvedFinancialsQ.data?.tin.source ?? null;
  const partBIsOverride = partBSource === 'manual override';
  const partBIsCms = !!partBSource && !partBIsOverride && partBSource !== 'absent'
    && partBSource !== 'QPP file';
  const partBIsQppFile = partBSource === 'QPP file';

  // Keep the Profile field in lockstep with CMS-resolved $ when not overridden
  // (hydrate writes practice_settings; this mirrors it into local form state).
  useEffect(() => {
    if (partBIsOverride) return;
    const resolved = resolvedFinancialsQ.data?.tin.resolved;
    if (resolved == null) return;
    setPracticeInfo(prev => {
      const cur = prev.medicarePartBCharges !== '' && prev.medicarePartBCharges != null
        ? Number(prev.medicarePartBCharges) : null;
      if (cur != null && Math.abs(cur - Number(resolved)) < 0.5) return prev;
      return { ...prev, medicarePartBCharges: String(Math.round(Number(resolved))) };
    });
  }, [resolvedFinancialsQ.data?.tin.resolved, partBIsOverride]);
  
  // Get instant measure data from MipsDataService
  const dataService = MipsDataService.getInstance();
  
  // Measures for the VIEWING year — recomputed whenever the year changes so the
  // Configuration screen always reflects that year's active-measures list.
  const qualityMeasures = useMemo<CmsMeasure[]>(
    () => dataService.getActiveMeasuresByCategory('quality', programYear),
    [dataService, programYear]
  );
  const iaMeasures = useMemo<CmsMeasure[]>(
    () => dataService.getActiveMeasuresByCategory('ia', programYear),
    [dataService, programYear]
  );
  const piMeasures = useMemo<CmsMeasure[]>(
    () => dataService.getActiveMeasuresByCategory('pi', programYear),
    [dataService, programYear]
  );
  
  console.log('[Config] Instant measures loaded:', {
    qualityCount: qualityMeasures.length,
    iaCount: iaMeasures.length,
    piCount: piMeasures.length
  });
  
  // Selected measures and saving state
  const [selectedMeasures, setSelectedMeasures] = useState<Set<string>>(new Set());
  const [savingSettings, setSavingSettings] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  
  // Separate loading states for async operations only
  const [practiceSettingsLoading, setPracticeSettingsLoading] = useState(true);
  const [selectedMeasuresLoading, setSelectedMeasuresLoading] = useState(true);
  const [configPageReadyDelayDone, setConfigPageReadyDelayDone] = useState(false); // tiny buffer to smooth reveal
  
  // Practice Information State - will be loaded from database
  // Initialize with cached values to prevent UI jumps
  const [practiceInfo, setPracticeInfo] = useState(() => {
    const cached = userStorage.getItem(`practiceInfo:${selectedPracticeId}`);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        console.warn('Failed to parse cached practice info:', e);
      }
    }
    return {
      medicarePartBCharges: '',
      patientComplexityScore: 5,
      isSmallPractice: false,
      isASCOrHospital: false,
      isNonPatientFacing: false,
      isHPSAOrRural: false
    };
  });
  
  // Category Reweighting State - will be loaded from database
  const [reweightScenario, setReweightScenario] = useState('no-reweight');
  
  // Modeled Scores State
  const [modeledScores, setModeledScores] = useState<{
    piPercent?: number;
    iaPercent?: number;
    costPercent?: number;
  }>({});
  
  // Independent toggles for using modeled scores vs actual scores per category
  const [useModeledPi, setUseModeledPi] = useState(true);
  const [useModeledIa, setUseModeledIa] = useState(true);
  const [useModeledCost, setUseModeledCost] = useState(true);
  
  // Eligibility import state
  const [eligibilityImported, setEligibilityImported] = useState(false);
  const [configTab, setConfigTabState] = useState<ConfigLeafTab>(() => hashTab);

  // Deep-link sync: honor #mvps / #qpp-reports / etc. even while keep-mounted.
  // Done DURING render (React "adjusting state while rendering") — a useEffect
  // fires after paint, which flashed the stale tab (QPP Reports) for a frame
  // before jumping to the linked tab.
  const [lastSyncedHash, setLastSyncedHash] = useState(hash);
  if (hash !== lastSyncedHash) {
    setLastSyncedHash(hash);
    if (isConfigLeafTab(hash) && hash !== configTab) {
      setConfigTabState(hash);
    }
  }

  const setConfigTab = useCallback((tab: ConfigLeafTab) => {
    setConfigTabState(tab);
    const current = location.hash.replace(/^#/, '');
    if (current !== tab) {
      navigate(
        { pathname: location.pathname, search: location.search, hash: tab },
        { replace: true },
      );
    }
  }, [navigate, location.pathname, location.search, location.hash]);

  const primaryTab =
    isPracticeGroupLeaf(configTab) ? 'practice'
    : isReportingLeaf(configTab) ? 'mips-categories'
    : configTab;
  const primaryChipClass = (active: boolean) =>
    cn(
      'px-1.5 2xl:px-4 py-3 text-xs 2xl:text-sm font-medium rounded-lg transition-colors justify-center',
      active
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
    );
  const categoryTabClass = (active: boolean) =>
    cn(
      'rounded-none border-b-2 bg-transparent px-4 py-2.5 text-sm font-medium shadow-none transition-colors',
      active
        ? 'border-primary text-primary'
        : 'border-transparent text-slate-500 hover:text-slate-800'
    );
  const [eligibilityImportedAt, setEligibilityImportedAt] = useState<string | null>(null);
  
  // Calculate weights based on practice type and reweighting scenario
  const currentWeights = useMemo(() => {
    const practiceType = practiceInfo.isSmallPractice ? 'small' : 'large';
    return REWEIGHT_ALLOCATION[reweightScenario as keyof typeof REWEIGHT_ALLOCATION][practiceType];
  }, [practiceInfo.isSmallPractice, reweightScenario]);

  // Determine recommended reweighting based on practice characteristics
  // This is used for display/guidance only, NOT for auto-applying changes
  const getRecommendedReweightScenario = useMemo(() => {
    if (practiceInfo.isSmallPractice || practiceInfo.isASCOrHospital || practiceInfo.isNonPatientFacing) {
      return 'pi-reweight';
    }
    return 'no-reweight';
  }, [practiceInfo.isSmallPractice, practiceInfo.isASCOrHospital, practiceInfo.isNonPatientFacing]);

  // NOTE: Removed auto-apply useEffect that was causing settings to be overwritten
  // The reweight scenario is now ONLY changed when the user explicitly selects a new value
  // or when practice characteristics change via updatePracticeInfoWithReweight (see below)
  
  // Sync category weights to database when reweight scenario changes
  // This ensures practice_weights table has the correct weights for score calculations
  const syncWeightsToDatabase = async (scenario: string, isSmallPractice: boolean) => {
    const practiceType = isSmallPractice ? 'small' : 'large';
    const weights = REWEIGHT_ALLOCATION[scenario as keyof typeof REWEIGHT_ALLOCATION]?.[practiceType];
    
    if (!weights) {
      console.error('[Config] Invalid reweight scenario:', scenario);
      return;
    }
    
    console.log('[Config] Syncing weights to database:', { scenario, practiceType, weights });
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          quality_weight: weights.quality,
          cost_weight: weights.cost,
          ia_weight: weights.ia,
          pi_weight: weights.pi,
          // Preserve existing modeled scores
          modeled_pi_percent: modeledScores.piPercent,
          modeled_ia_percent: modeledScores.iaPercent,
          modeled_cost_percent: modeledScores.costPercent,
          use_modeled_pi: useModeledPi,
          use_modeled_ia: useModeledIa,
          use_modeled_cost: useModeledCost
        })
      });
      
      if (!response.ok) {
        console.error('[Config] Failed to sync weights to database');
      } else {
        console.log('[Config] Weights synced successfully');
        invalidatePracticeScoring(selectedPracticeId);
      }
    } catch (error) {
      console.error('[Config] Error syncing weights:', error);
    }
  };

  // Cache practice info whenever it changes (after initial load completes)
  useEffect(() => {
    if (selectedPracticeId && !practiceSettingsLoading) {
      // Cache current state to prevent UI jumps on navigation
      userStorage.setItem(`practiceInfo:${selectedPracticeId}`, JSON.stringify(practiceInfo));
    }
  }, [practiceInfo, selectedPracticeId, practiceSettingsLoading]);

  // API URL configuration
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Load async data (practice settings and selected measures)
  useEffect(() => {
    if (!selectedPracticeId) {
      console.log('[Config] No practice selected');
      setPracticeSettingsLoading(false);
      setSelectedMeasuresLoading(false);
      return;
    }
    
    console.log('[Config] Loading async data for practice:', selectedPracticeId);
    
    const loadAsyncData = async () => {
      console.log('[Config] Loading async data...');
      
      try {
        // Load saved selections from localStorage first (instant)
        try {
          const saved = localStorage.getItem('measureSelection:selected');
          if (saved) {
            setSelectedMeasures(new Set(JSON.parse(saved)));
          }
          setSelectedMeasuresLoading(false);
        } catch (e) {
          console.error('Failed to load saved measure selections', e);
          setSelectedMeasuresLoading(false);
        }
        
        // Load practice settings from API (async)
        console.log('[Config] Fetching practice settings...');
        const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/settings?year=${programYear}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        });
        
        if (response.ok) {
          const settings = await response.json();
          
          // Batch all state updates together
          setPracticeInfo({
            medicarePartBCharges: settings.medicare_part_b_charges || '',
            patientComplexityScore: settings.patient_complexity_score ?? 5,
            isSmallPractice: settings.is_small_practice || false,
            isASCOrHospital: settings.is_asc_hospital || false,
            isNonPatientFacing: settings.is_non_patient_facing || false,
            isHPSAOrRural: settings.is_hpsa_rural || false
          });
          
          // Set reweight scenario from database - respect user's saved choice
          // Do NOT auto-override based on practice characteristics - user may have intentionally set a different value
          setReweightScenario(settings.reweight_scenario || 'no-reweight');
          
          // Set eligibility import status
          setEligibilityImported(settings.eligibility_imported || false);
          setEligibilityImportedAt(settings.eligibility_imported_at || null);
        } else {
          console.error('Failed to load practice settings');
          toast({
            title: "Error",
            description: "Failed to load practice settings",
            variant: "destructive",
          });
        }
        
        // Load MIPS weights and modeled scores
        const weightsResponse = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        });
        
        if (weightsResponse.ok) {
          const weightsData = await weightsResponse.json();
          
          // Update modeled scores and individual toggle states
          setModeledScores({
            piPercent: weightsData.modeled_pi_percent,
            iaPercent: weightsData.modeled_ia_percent,
            costPercent: weightsData.modeled_cost_percent
          });
          
          // Update individual toggle states
          // Default to false (Calculate mode) if database has null - user hasn't set a preference yet
          // This prevents accidentally resetting to "Enter Estimate" mode
          setUseModeledPi(weightsData.use_modeled_pi === true);
          setUseModeledIa(weightsData.use_modeled_ia === true);
          setUseModeledCost(weightsData.use_modeled_cost === true);
        }
        
        // Mark settings as loaded
        console.log('[Config] Practice settings loaded');
        setPracticeSettingsLoading(false);
        
      } catch (error) {
        console.error('[Config] Error loading configuration data:', error);
        toast({
          title: "Error",
          description: "Failed to load configuration data. Using cached values.",
          variant: "destructive",
        });
        // Mark as loaded even on error
        setPracticeSettingsLoading(false);
      }
    };
    
    loadAsyncData();
  }, [selectedPracticeId, toast, apiUrl, programYear]);
  
  
  // Removed automatic reweight effect - now handled directly in loadPracticeSettings to prevent jumps

  // Save practice settings to database
  const savePracticeSettings = async () => {
    if (!selectedPracticeId) {
      toast({
        title: "Error",
        description: "No practice selected",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setSavingSettings(true);
      
      const settingsData = {
        medicare_part_b_charges: practiceInfo.medicarePartBCharges || null,
        patient_complexity_score: practiceInfo.patientComplexityScore,
        is_small_practice: practiceInfo.isSmallPractice,
        is_asc_hospital: practiceInfo.isASCOrHospital,
        is_non_patient_facing: practiceInfo.isNonPatientFacing,
        is_hpsa_rural: practiceInfo.isHPSAOrRural,
        reweight_scenario: reweightScenario
      };
      
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/settings?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify(settingsData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save practice settings');
      }
      
      // Validate weights sum to 100
      const weightSum = currentWeights.quality + currentWeights.cost + currentWeights.ia + currentWeights.pi;
      if (Math.abs(weightSum - 100) > 0.01) {
        toast({
          title: "Error",
          description: `Category weights must sum to 100% (current sum: ${weightSum}%)`,
          variant: "destructive",
        });
        setSavingSettings(false);
        return;
      }
      
      // Validate modeled percentages are within range
      if (modeledScores.piPercent !== undefined && (modeledScores.piPercent < 0 || modeledScores.piPercent > 100)) {
        toast({
          title: "Error",
          description: "PI percentage must be between 0 and 100",
          variant: "destructive",
        });
        setSavingSettings(false);
        return;
      }
      
      if (modeledScores.iaPercent !== undefined && (modeledScores.iaPercent < 0 || modeledScores.iaPercent > 100)) {
        toast({
          title: "Error",
          description: "IA percentage must be between 0 and 100",
          variant: "destructive",
        });
        setSavingSettings(false);
        return;
      }
      
      if (modeledScores.costPercent !== undefined && (modeledScores.costPercent < 0 || modeledScores.costPercent > 100)) {
        toast({
          title: "Error",
          description: "Cost percentage must be between 0 and 100",
          variant: "destructive",
        });
        setSavingSettings(false);
        return;
      }
      
      // Save MIPS weights and modeled scores with independent toggles
      const weightsData = {
        quality_weight: currentWeights.quality,
        cost_weight: currentWeights.cost,
        ia_weight: currentWeights.ia,
        pi_weight: currentWeights.pi,
        modeled_pi_percent: modeledScores.piPercent,
        modeled_ia_percent: modeledScores.iaPercent,
        modeled_cost_percent: modeledScores.costPercent,
        use_modeled_pi: useModeledPi,
        use_modeled_ia: useModeledIa,
        use_modeled_cost: useModeledCost
      };
      
      const weightsResponse = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify(weightsData),
      });
      
      if (!weightsResponse.ok) {
        const error = await weightsResponse.json();
        throw new Error(error.error || 'Failed to save MIPS weights');
      }

      invalidatePracticeScoring(selectedPracticeId);

      // Refresh IA requirements so the IA tab immediately reflects special status (e.g., small practice)
      try {
        const refreshedIaReqs = await getIARequirements(selectedPracticeId, { year: programYear });
        if (refreshedIaReqs) {
          setIaRequirements(refreshedIaReqs);
        }
      } catch (err) {
        console.error('[Config] Unable to refresh IA requirements after saving settings:', err);
        // Non-blocking: keep existing state if refresh fails
      }

      syncPracticeSettingsCache(selectedPracticeId, programYear, settingsData);
      
      toast({
        title: "Success",
        description: "Practice settings and MIPS weights saved successfully",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to save settings";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSavingSettings(false);
    }
  };
  
  // Update practice info in state with instant auto-save
  // When reweight-affecting flags change, also updates reweight scenario atomically
  const updatePracticeInfo = async (key: string, value: any) => {
    const newInfo = { ...practiceInfo, [key]: value };
    setPracticeInfo(newInfo);
    
    console.log('[Config] updatePracticeInfo called:', { key, value, newInfo });
    
    // Check if this change affects reweight eligibility
    const reweightAffectingKeys = ['isSmallPractice', 'isASCOrHospital', 'isNonPatientFacing'];
    const affectsReweight = reweightAffectingKeys.includes(key);
    
    // Calculate the new appropriate reweight scenario based on updated practice info
    let newReweightScenario = reweightScenario;
    if (affectsReweight) {
      const shouldHavePiReweight = newInfo.isSmallPractice || newInfo.isASCOrHospital || newInfo.isNonPatientFacing;
      
      // If user had pi-reweight and now should NOT have it (turned off all flags), change to no-reweight
      // If user didn't have pi-reweight and now SHOULD have it (turned on a flag), change to pi-reweight
      if (shouldHavePiReweight && reweightScenario === 'no-reweight') {
        newReweightScenario = 'pi-reweight';
      } else if (!shouldHavePiReweight && reweightScenario === 'pi-reweight') {
        newReweightScenario = 'no-reweight';
      }
      // Note: If user has a custom reweight (like cost-reweight), we don't change it
      
      if (newReweightScenario !== reweightScenario) {
        console.log('[Config] Reweight scenario changing from', reweightScenario, 'to', newReweightScenario);
        setReweightScenario(newReweightScenario);
      }
    }
    
    // Save instantly - include the correct reweight scenario in the SAME save
    console.log('[Config] Auto-save triggered for practice info');
    setAutoSaving(true);
    try {
      const settingsData = {
        medicare_part_b_charges: newInfo.medicarePartBCharges || null,
        patient_complexity_score: newInfo.patientComplexityScore,
        is_small_practice: newInfo.isSmallPractice,
        is_asc_hospital: newInfo.isASCOrHospital,
        is_non_patient_facing: newInfo.isNonPatientFacing,
        is_hpsa_rural: newInfo.isHPSAOrRural,
        reweight_scenario: newReweightScenario  // Use the NEW scenario, not stale state
      };
      
      console.log('[Config] Auto-saving settings:', settingsData);
      
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/settings?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settingsData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Config] Auto-save failed:', response.status, errorText);
        throw new Error('Failed to auto-save practice settings');
      }
      
      console.log('[Config] Auto-save successful');
      syncPracticeSettingsCache(selectedPracticeId, programYear, settingsData);
      toast({
        title: "Saved",
        description: "Practice settings saved successfully.",
      });

      // If special-status flags changed, refresh IA requirements so IA tab reflects current activitiesRequired
      if (affectsReweight) {
        try {
          const refreshedIaReqs = await getIARequirements(selectedPracticeId, { year: programYear });
          if (refreshedIaReqs) {
            setIaRequirements(refreshedIaReqs);
          }
        } catch (refreshErr) {
          console.error('[Config] Unable to refresh IA requirements after auto-save:', refreshErr);
          // Non-blocking: keep existing state if refresh fails
        }
      }
      
      // If special-status flags changed, ensure weights are synced to practice_weights table
      // Always sync on special-status changes to keep zero-weight PI (or other reweights) current
      if (affectsReweight) {
        await syncWeightsToDatabase(newReweightScenario, newInfo.isSmallPractice);
      }
    } catch (error) {
      console.error('[Config] Auto-save error:', error);
      toast({
        title: "Auto-save failed",
        description: "Failed to save practice settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAutoSaving(false);
    }
  };
  
  // Update use modeled toggle with instant auto-save
  const updateUseModeled = async (category: 'pi' | 'ia' | 'cost', value: boolean) => {
    console.log('[Config] updateUseModeled called:', { category, value });
    
    // Update the appropriate state
    if (category === 'pi') setUseModeledPi(value);
    if (category === 'ia') setUseModeledIa(value);
    if (category === 'cost') setUseModeledCost(value);
    
    // Save instantly
    console.log('[Config] Auto-save triggered for use modeled toggle');
    setAutoSaving(true);
    try {
      const weightsData = {
        quality_weight: currentWeights.quality,
        cost_weight: currentWeights.cost,
        ia_weight: currentWeights.ia,
        pi_weight: currentWeights.pi,
        modeled_pi_percent: modeledScores.piPercent,
        modeled_ia_percent: modeledScores.iaPercent,
        modeled_cost_percent: modeledScores.costPercent,
        use_modeled_pi: category === 'pi' ? value : useModeledPi,
        use_modeled_ia: category === 'ia' ? value : useModeledIa,
        use_modeled_cost: category === 'cost' ? value : useModeledCost
      };
      
      console.log('[Config] Auto-saving use modeled toggles:', weightsData);
      
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(weightsData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Config] Auto-save failed:', response.status, errorText);
        throw new Error('Failed to auto-save use modeled toggle');
      }
      
      console.log('[Config] Auto-save successful');
      invalidatePracticeScoring(selectedPracticeId);
      toast({
        title: "Saved",
        description: "Model toggle saved successfully.",
      });
    } catch (error) {
      console.error('[Config] Auto-save error:', error);
      toast({
        title: "Auto-save failed",
        description: "Failed to save model toggle. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAutoSaving(false);
    }
  };

  // Update modeled scores with validation and instant auto-save
  const updateModeledScore = async (field: 'piPercent' | 'iaPercent' | 'costPercent', value: number | undefined) => {
    // Validate and cap value between 0 and 100
    const validatedValue = value !== undefined 
      ? Math.min(100, Math.max(0, value))
      : undefined;
    
    const newScores = {
      ...modeledScores,
      [field]: validatedValue
    };
    
    setModeledScores(newScores);
    
    console.log('[Config] updateModeledScore called:', { field, value: validatedValue });
    
    // Save instantly
    console.log('[Config] Auto-save triggered for modeled scores');
    setAutoSaving(true);
    try {
      const weightsData = {
        quality_weight: currentWeights.quality,
        cost_weight: currentWeights.cost,
        ia_weight: currentWeights.ia,
        pi_weight: currentWeights.pi,
        modeled_pi_percent: newScores.piPercent,
        modeled_ia_percent: newScores.iaPercent,
        modeled_cost_percent: newScores.costPercent,
        use_modeled_pi: useModeledPi,
        use_modeled_ia: useModeledIa,
        use_modeled_cost: useModeledCost
      };
      
      console.log('[Config] Auto-saving modeled scores:', weightsData);
      
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(weightsData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Config] Auto-save failed:', response.status, errorText);
        throw new Error('Failed to auto-save modeled scores');
      }
      
      console.log('[Config] Auto-save successful');
      invalidatePracticeScoring(selectedPracticeId);
      toast({
        title: "Saved",
        description: "Model scores saved successfully.",
      });
    } catch (error) {
      console.error('[Config] Auto-save error:', error);
      toast({
        title: "Auto-save failed",
        description: "Failed to save model scores. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAutoSaving(false);
    }
  };

  // Update reweight scenario in state with instant auto-save
  // Also syncs the category weights to database
  const updateReweightScenario = async (scenario: string) => {
    const previousScenario = reweightScenario;
    setReweightScenario(scenario);
    
    console.log('[Config] updateReweightScenario called:', scenario);
    
    // Save instantly
    console.log('[Config] Auto-save triggered for reweight scenario');
    setAutoSaving(true);
    try {
      const settingsData = {
        medicare_part_b_charges: practiceInfo.medicarePartBCharges || null,
        patient_complexity_score: practiceInfo.patientComplexityScore,
        is_small_practice: practiceInfo.isSmallPractice,
        is_asc_hospital: practiceInfo.isASCOrHospital,
        is_non_patient_facing: practiceInfo.isNonPatientFacing,
        is_hpsa_rural: practiceInfo.isHPSAOrRural,
        reweight_scenario: scenario
      };
      
      console.log('[Config] Auto-saving settings:', settingsData);
      
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/settings?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settingsData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Config] Auto-save failed:', response.status, errorText);
        throw new Error('Failed to auto-save reweight scenario');
      }
      
      console.log('[Config] Auto-save successful');
      syncPracticeSettingsCache(selectedPracticeId, programYear, settingsData);
      toast({
        title: "Saved",
        description: "Reweight scenario saved successfully.",
      });
      
      // Sync the new category weights to database
      if (scenario !== previousScenario) {
        await syncWeightsToDatabase(scenario, practiceInfo.isSmallPractice);
      }
    } catch (error) {
      console.error('[Config] Auto-save error:', error);
      toast({
        title: "Auto-save failed",
        description: "Failed to save reweight scenario. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAutoSaving(false);
    }
  };
  
  // Filters
  const [filters, setFilters] = useState({
    collectionType: '',
    specialty: '',
    bonusEligible: false,
    outcome: false
  });

  // IA-specific filters
  const [iaSubcategory, setIaSubcategory] = useState('all');
  const [iaSearchQuery, setIaSearchQuery] = useState('');
  
  // IA selections from database
  const [iaSelections, setIaSelections] = useState<IASelection[]>([]);
  const [iaRequirements, setIaRequirements] = useState<IARequirements | null>(null);
  const [iaSelectionsLoading, setIaSelectionsLoading] = useState(true);
  const [iaSavingSelection, setIaSavingSelection] = useState<string | null>(null);

  // Save selections to localStorage (for Quality measures)
  useEffect(() => {
    localStorage.setItem('measureSelection:selected', JSON.stringify(Array.from(selectedMeasures)));
  }, [selectedMeasures]);

  // Load IA selections and requirements from database
  useEffect(() => {
    if (!selectedPracticeId) {
      setIaSelectionsLoading(false);
      return;
    }
    
    const loadIAData = async () => {
      setIaSelectionsLoading(true);
      try {
        const [selections, requirements] = await Promise.all([
          getIASelections(selectedPracticeId, { year: programYear }),
          getIARequirements(selectedPracticeId, { year: programYear })
        ]);
        setIaSelections(selections);
        setIaRequirements(requirements);
      } catch (error) {
        console.error('Error loading IA data:', error);
      } finally {
        setIaSelectionsLoading(false);
      }
    };
    
    loadIAData();
  }, [selectedPracticeId, programYear]);

  // Toggle IA selection (saves to database)
  const toggleIASelection = async (measureId: string) => {
    if (!selectedPracticeId) return;
    
    const isSelected = iaSelections.some(s => s.ia_activity_id === measureId);
    setIaSavingSelection(measureId);
    
    try {
      if (isSelected) {
        // Remove selection
        const success = await removeIASelection(selectedPracticeId, measureId, { year: programYear });
        if (success) {
          setIaSelections(prev => prev.filter(s => s.ia_activity_id !== measureId));
          toast({
            title: "Activity Removed",
            description: `${measureId} removed from your IA plan.`,
          });
        }
      } else {
        // Add selection
        const result = await addIASelection(selectedPracticeId, measureId, { year: programYear });
        if (result) {
          setIaSelections(prev => [...prev, result]);
          toast({
            title: "Activity Added",
            description: `${measureId} added to your IA plan.`,
          });
        }
      }
    } catch (error) {
      console.error('Error toggling IA selection:', error);
      toast({
        title: "Error",
        description: "Failed to update IA selection",
        variant: "destructive",
      });
    } finally {
      setIaSavingSelection(null);
    }
  };
  
  // Filter quality measures
  const filteredQualityMeasures = useMemo(() => {
    const dataService = MipsDataService.getInstance();
    
    return qualityMeasures.filter(measure => {
      const meta = dataService.getMeasureMeta(measure.measureId);
      
      // Collection type filter
      if (filters.collectionType && !meta.collectionType.toLowerCase().includes(filters.collectionType.toLowerCase())) {
        return false;
      }
      
      // Bonus eligible filter
      if (filters.bonusEligible && !meta.autoPts) {
        return false;
      }
      
      // Outcome measure filter
      if (filters.outcome && !['outcome', 'intermediateOutcome', 'patientReportedOutcome'].includes(measure.measureType || '')) {
        return false;
      }
      
      return true;
    });
  }, [qualityMeasures, filters]);

  // Filter IA measures by subcategory and search
  const filteredIaMeasures = useMemo(() => {
    return iaMeasures.filter(measure => {
      // Subcategory filter
      if (iaSubcategory && iaSubcategory !== 'all') {
        const measureSubcat = getSubcategoryIdFromActivityId(measure.measureId);
        if (measureSubcat !== iaSubcategory) {
          return false;
        }
      }
      
      // Search filter
      if (iaSearchQuery.trim()) {
        const query = iaSearchQuery.toLowerCase();
        const matchesId = measure.measureId.toLowerCase().includes(query);
        const matchesTitle = measure.title?.toLowerCase().includes(query);
        const matchesDesc = measure.description?.toLowerCase().includes(query);
        if (!matchesId && !matchesTitle && !matchesDesc) {
          return false;
        }
      }
      
      return true;
    });
  }, [iaMeasures, iaSubcategory, iaSearchQuery]);
  
  const toggleMeasureSelection = (measureId: string) => {
    setSelectedMeasures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(measureId)) {
        newSet.delete(measureId);
      } else {
        newSet.add(measureId);
      }
      return newSet;
    });
  };
  
  // Calculate selection summary
  const selectionSummary = useMemo(() => {
    const selectedQuality = qualityMeasures.filter(m => selectedMeasures.has(m.measureId));
    const hasOutcome = selectedQuality.some(m => m.isOutcomeMeasure === true);
    
    return {
      total: selectedMeasures.size,
      quality: selectedQuality.length,
      hasOutcome,
      needsOutcome: selectedQuality.length >= 6 && !hasOutcome
    };
  }, [qualityMeasures, selectedMeasures]);

  // Unified loading gate - show full page skeleton until core data is ready
  console.log('[CONFIG] Loading states:', { 
    practiceSettingsLoading, 
    selectedMeasuresLoading,
    selectedPracticeId 
  });

  const configPageReady = !practiceSettingsLoading;

  useEffect(() => {
    if (configPageReady) {
      const timer = setTimeout(() => setConfigPageReadyDelayDone(true), 75);
      return () => clearTimeout(timer);
    }
    setConfigPageReadyDelayDone(false);
  }, [configPageReady]);
  
  if (!configPageReady || !configPageReadyDelayDone) {
    console.log('[CONFIG] Showing skeleton - practiceSettingsLoading is true');
    return (
      <>
        <Helmet>
          <title>MIPS Configuration – Quality, PI, IA, Practice Details</title>
          <meta name="description" content="Configure MIPS: select Quality/PI/IA measures and set practice details including category reweighting." />
          <link rel="canonical" href="/measure-selection" />
        </Helmet>
        <ConfigurationFullPageSkeleton activeTab={configTab} />
      </>
    );
  }
  
  if (!selectedPracticeId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Please select a practice from the dropdown above</div>
      </div>
    );
  }

  // Page structure is always ready - measures are instant!
  console.log('[Config] Rendering page with targeted loading states');

  return (
    <div>
      <Helmet>
        <title>MIPS Configuration – Quality, PI, IA, Practice Details</title>
        <meta name="description" content="Configure MIPS: select Quality/PI/IA measures and set practice details including category reweighting." />
        <link rel="canonical" href="/measure-selection" />
      </Helmet>

      <h1 className="mb-4 text-3xl font-bold">MIPS Configuration</h1>

      <div className="mb-4">
        <YearLockBanner />
      </div>

      <div className="space-y-4">
        <div className="grid w-full grid-cols-4 gap-1 2xl:gap-2">
          <button
            type="button"
            className={primaryChipClass(primaryTab === 'qpp-reports')}
            onClick={() => setConfigTab('qpp-reports')}
          >
            <span className="hidden 2xl:inline">QPP Reports</span>
            <span className="2xl:hidden">QPP</span>
          </button>
          <button
            type="button"
            className={primaryChipClass(primaryTab === 'practice')}
            onClick={() => setConfigTab(isPracticeGroupLeaf(configTab) ? configTab : 'practice')}
          >
            Practice
          </button>
          <button
            type="button"
            className={primaryChipClass(primaryTab === 'mips-categories')}
            onClick={() => setConfigTab(isReportingLeaf(configTab) ? configTab : 'quality')}
          >
            <span className="hidden 2xl:inline">Manage MIPS Categories</span>
            <span className="2xl:hidden">Manage Categories</span>
          </button>
          <button
            type="button"
            className={primaryChipClass(primaryTab === 'mvps')}
            onClick={() => setConfigTab('mvps')}
          >
            <span className="hidden 2xl:inline">Manage MVPs</span>
            <span className="2xl:hidden">MVPs</span>
          </button>
        </div>

        {isReportingLeaf(configTab) && (
        <div className="inline-flex h-auto w-full justify-start gap-0 border-b border-slate-200">
          <button type="button" className={categoryTabClass(configTab === 'quality')} onClick={() => setConfigTab('quality')}>
            Quality
          </button>
          <button type="button" className={categoryTabClass(configTab === 'pi')} onClick={() => setConfigTab('pi')}>
            <span className="hidden 2xl:inline">Promoting Interoperability</span>
            <span className="2xl:hidden">PI</span>
          </button>
          <button type="button" className={categoryTabClass(configTab === 'cost')} onClick={() => setConfigTab('cost')}>
            Cost
          </button>
          <button type="button" className={categoryTabClass(configTab === 'ia')} onClick={() => setConfigTab('ia')}>
            <span className="hidden 2xl:inline">Improvement Activities</span>
            <span className="2xl:hidden">IA</span>
          </button>
        </div>
        )}

        {/* Panels stay mounted (hidden) to avoid remount flash on tab switch */}
        <div className={cn('space-y-4', configTab !== 'qpp-reports' && 'hidden')}>
          <QPPReportsTab
            practiceId={selectedPracticeId}
            programYear={programYear}
            eligibilityImported={eligibilityImported}
            eligibilityImportedAt={eligibilityImportedAt}
            onEligibilityImportComplete={(mappedSettings) => {
              // Update practice info with imported values
              setPracticeInfo({
                medicarePartBCharges: mappedSettings.medicare_part_b_charges || '',
                patientComplexityScore: practiceInfo.patientComplexityScore, // Keep existing
                isSmallPractice: mappedSettings.is_small_practice || false,
                isASCOrHospital: mappedSettings.is_asc_hospital || false,
                isNonPatientFacing: mappedSettings.is_non_patient_facing || false,
                isHPSAOrRural: mappedSettings.is_hpsa_rural || false
              });
              if (selectedPracticeId) {
                syncPracticeSettingsCache(selectedPracticeId, programYear, {
                  medicare_part_b_charges: mappedSettings.medicare_part_b_charges ?? null,
                  is_small_practice: mappedSettings.is_small_practice || false,
                  is_asc_hospital: mappedSettings.is_asc_hospital || false,
                  is_non_patient_facing: mappedSettings.is_non_patient_facing || false,
                  is_hpsa_rural: mappedSettings.is_hpsa_rural || false,
                  reweight_scenario: mappedSettings.reweight_scenario,
                  eligibility_imported: true,
                  eligibility_imported_at: new Date().toISOString(),
                });
              }
              // Update reweight scenario
              if (mappedSettings.reweight_scenario) {
                setReweightScenario(mappedSettings.reweight_scenario);
              }
              // Mark as imported
              setEligibilityImported(true);
              setEligibilityImportedAt(new Date().toISOString());
            }}
            onEligibilityClearImport={() => {
              setEligibilityImported(false);
              setEligibilityImportedAt(null);
            }}
          />
        </div>

        <div className={cn('space-y-4', !isPracticeGroupLeaf(configTab) && 'hidden')}>
          <Tabs
            value={configTab === 'preferences' ? 'preferences' : 'practice'}
            onValueChange={(v) => {
              if (v === 'practice' || v === 'preferences') setConfigTab(v);
            }}
            className="space-y-0"
          >
            <TabsList className="inline-flex h-auto w-full justify-start gap-0 rounded-none bg-transparent p-0 border-b border-slate-200">
              <TabsTrigger value="practice" className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2.5 text-sm font-medium text-slate-500 shadow-none hover:text-slate-800 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none">
                Profile
              </TabsTrigger>
              <TabsTrigger value="preferences" className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2.5 text-sm font-medium text-slate-500 shadow-none hover:text-slate-800 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none">
                Preferences
              </TabsTrigger>
            </TabsList>

            <TabsContent value="practice" forceMount className="space-y-4 mt-0 pt-4 data-[state=inactive]:hidden">
          {/* Configuration Notice */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-slate-500 mt-0.5">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Settings Overview</h3>
                <p className="text-sm text-slate-700">
                  These settings determine how MIPS scoring calculations are performed across all reports and dashboards. Category weights automatically adjust based on your practice characteristics and selected reweighting scenario per CMS guidelines.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Practice Profile</CardTitle>
                <CardDescription>Basic inputs used for scoring and thresholds.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Practice Inputs */}
                <div className="space-y-4">
                  <Label className="grid gap-2">
                    <span className="flex items-center gap-2">
                      Medicare Part B Charges
                      {partBIsOverride && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-normal">Override</span>
                      )}
                      {!partBIsOverride && partBIsCms && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded font-normal">CMS</span>
                      )}
                      {!partBIsOverride && (partBIsQppFile || (eligibilityImported && practiceInfo.medicarePartBCharges && !partBIsCms)) && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-normal">QPP</span>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="rounded-l border bg-muted px-3 py-2 text-sm">$</span>
                      <Input 
                        type="text" 
                        placeholder="0" 
                        className="flex-1"
                        value={practiceInfo.medicarePartBCharges ? Number(practiceInfo.medicarePartBCharges).toLocaleString() : ''}
                        onChange={(e) => {
                          // Remove commas and non-numeric characters (except decimal point)
                          const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                          updatePracticeInfo('medicarePartBCharges', numericValue);
                        }}
                        disabled={practiceSettingsLoading}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Same figure as QPP Reports → Medicare charges. CMS refresh updates this;
                      editing a different amount stamps an override that survives refresh.
                    </span>
                  </Label>
                  
                  <div>
                    <div className="mb-2 text-sm font-medium">Patient Complexity Score: {practiceInfo.patientComplexityScore}</div>
                    <Slider 
                      value={[practiceInfo.patientComplexityScore]} 
                      min={0} 
                      max={10} 
                      step={1}
                      onValueChange={(value) => updatePracticeInfo('patientComplexityScore', value[0])}
                      disabled={practiceSettingsLoading}
                    />
                    <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                      <span>Low (0)</span>
                      <span>High (10)</span>
                    </div>
                  </div>
                </div>

                {/* Practice Characteristics */}
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    Practice Characteristics
                    {eligibilityImported && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-normal">QPP</span>
                    )}
                  </h4>
                  <div className="grid gap-3 sm:grid-cols-1 lg:grid-cols-2">
                    <div className="flex items-center justify-between rounded border p-3">
                      <div>
                        <div className="text-sm font-medium">Small Practice</div>
                        <div className="text-xs text-muted-foreground">≤15 eligible clinicians</div>
                      </div>
                      <Switch 
                        aria-label="Small practice toggle" 
                        checked={practiceInfo.isSmallPractice}
                        onCheckedChange={(checked) => updatePracticeInfo('isSmallPractice', checked)}
                        disabled={practiceSettingsLoading}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between rounded border p-3">
                      <div>
                        <div className="text-sm font-medium">ASC or Hospital-based</div>
                        <div className="text-xs text-muted-foreground">Ambulatory surgical center or hospital</div>
                      </div>
                      <Switch 
                        aria-label="ASC or Hospital-based practice toggle" 
                        checked={practiceInfo.isASCOrHospital}
                        onCheckedChange={(checked) => updatePracticeInfo('isASCOrHospital', checked)}
                        disabled={practiceSettingsLoading}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between rounded border p-3">
                      <div>
                        <div className="text-sm font-medium">Non-patient Facing</div>
                        <div className="text-xs text-muted-foreground">Does not provide direct patient care</div>
                      </div>
                      <Switch 
                        aria-label="Non-patient facing practice toggle" 
                        checked={practiceInfo.isNonPatientFacing}
                        onCheckedChange={(checked) => updatePracticeInfo('isNonPatientFacing', checked)}
                        disabled={practiceSettingsLoading}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between rounded border p-3">
                      <div>
                        <div className="text-sm font-medium">HPSA or Rural-based</div>
                        <div className="text-xs text-muted-foreground">Located in underserved or rural area</div>
                      </div>
                      <Switch 
                        aria-label="HPSA or Rural practice toggle" 
                        checked={practiceInfo.isHPSAOrRural}
                        onCheckedChange={(checked) => updatePracticeInfo('isHPSAOrRural', checked)}
                        disabled={practiceSettingsLoading}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Category Weights</CardTitle>
                <CardDescription>Adjust category weights based on CMS policy.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Label className="grid gap-2">Reweighting Scenario
                  <Select value={reweightScenario} onValueChange={updateReweightScenario}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select scenario" />
                    </SelectTrigger>
                    <SelectContent className="z-50">
                      <SelectItem value="no-reweight">No Category Reweight</SelectItem>
                      <SelectItem value="cost-reweight">Cost Reweight</SelectItem>
                      <SelectItem value="pi-reweight">PI Reweight</SelectItem>
                      <SelectItem value="quality-reweight">Quality Reweight</SelectItem>
                      <SelectItem value="ia-reweight">IA Reweight</SelectItem>
                      <SelectItem value="cost-pi-reweight">Cost and PI Reweight</SelectItem>
                      <SelectItem value="cost-quality-reweight">Cost and Quality Reweight</SelectItem>
                      <SelectItem value="cost-ia-reweight">Cost and IA Reweight</SelectItem>
                      <SelectItem value="pi-quality-reweight">PI and Quality Reweight</SelectItem>
                      <SelectItem value="pi-ia-reweight">PI and IA Reweight</SelectItem>
                      <SelectItem value="quality-ia-reweight">Quality and IA Reweight</SelectItem>
                    </SelectContent>
                  </Select>
                </Label>


                {/* weights display */}
                <div className="space-y-2">
                  {[
                    { label: "Quality", value: currentWeights.quality },
                    { label: "Cost", value: currentWeights.cost },
                    { label: "IA", value: currentWeights.ia },
                    { label: "PI", value: currentWeights.pi },
                  ].map((w) => (
                    <div key={w.label} className="flex items-center justify-between rounded bg-muted p-2">
                      <span>{w.label}</span>
                      <Badge className="w-16 justify-center">{w.value}%</Badge>
                    </div>
                  ))}
                </div>
                
                {/* Show active reweighting triggers */}
                {reweightScenario !== 'no-reweight' && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-yellow-800 mb-1">Automatic Reweighting Applied</p>
                        <p className="text-xs text-yellow-700">
                          {[
                            practiceInfo.isSmallPractice && 'Small Practice (≤15 clinicians)',
                            practiceInfo.isASCOrHospital && 'ASC/Hospital-based setting',
                            practiceInfo.isNonPatientFacing && 'Non-patient facing practice'
                          ].filter(Boolean).join(', ')} automatically triggers PI Reweight per CMS guidelines. This can be overridden above if needed.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
          
          {/* Action Buttons - Outside cards for consistency with Preferences tab */}
          <div className="flex items-center justify-end gap-2 mt-6">
            {/* Auto-save indicator */}
            {autoSaving && (
              <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Auto-saving...</span>
              </div>
            )}
          </div>
            </TabsContent>

            <TabsContent value="preferences" forceMount className="space-y-4 mt-0 pt-4 data-[state=inactive]:hidden">
              <DisplayPreferencesCard />
              <ConflictPreferencesCard />
            </TabsContent>
          </Tabs>
        </div>

        {/* Category panels — keep mounted to avoid remount flash */}
        <div className={cn('space-y-4 pt-4', configTab !== 'quality' && 'hidden')}>
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
              <CardDescription>Refine by collection type and measure attributes.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <Label className="grid gap-2">Collection Type
                <Input 
                  placeholder="QCDR / MIPS CQM / eCQM" 
                  value={filters.collectionType}
                  onChange={(e) => setFilters(prev => ({ ...prev, collectionType: e.target.value }))}
                />
              </Label>
              <Label className="grid gap-2">Specialty
                <Input 
                  placeholder="Any" 
                  value={filters.specialty}
                  onChange={(e) => setFilters(prev => ({ ...prev, specialty: e.target.value }))}
                />
              </Label>
              <div className="flex items-center gap-4">
                <Label className="flex items-center gap-2 text-sm">
                  <Checkbox 
                    checked={filters.bonusEligible}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, bonusEligible: checked as boolean }))}
                  /> 
                  Bonus Eligible
                </Label>
                <Label className="flex items-center gap-2 text-sm">
                  <Checkbox 
                    checked={filters.outcome}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, outcome: checked as boolean }))}
                  /> 
                  Outcome
                </Label>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredQualityMeasures.map(measure => {
              const meta = MipsDataService.getInstance().getMeasureMeta(measure.measureId);
              const tags = [];
              
              // Add collection type tag
              if (meta.collectionType) tags.push(meta.collectionType);
              
              // Add high priority tag
              if (measure.isHighPriority) tags.push('High Priority');
              
              // Add outcome tag
              if (measure.isOutcomeMeasure) tags.push('Outcome');
              
              // Add bonus eligible tag
              if (meta.autoPts) tags.push('Bonus Eligible');
              
              // Add 7-pt cap tag
              if (meta.cap7) tags.push('7-pt Cap');
              
              return (
                <Card key={measure.measureId} className="flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-base">
                      {measure.measureId}: {measure.title}
                    </CardTitle>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {tags.map(t => <Badge key={t} variant="secondary">{t}</Badge>)}
                    </div>
                    <CardDescription className="mt-2 line-clamp-2">
                      {measure.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto">
                    <Button 
                      variant={selectedMeasures.has(measure.measureId) ? "default" : "secondary"} 
                      className="w-full"
                      onClick={() => toggleMeasureSelection(measure.measureId)}
                    >
                      {selectedMeasures.has(measure.measureId) ? "Selected" : "Select"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">
                  Measures selected: {selectionSummary.quality} Quality measures
                </div>
                {selectionSummary.needsOutcome && (
                  <div className="text-sm text-warning">
                    Missing: Outcome measure required (6+ measures selected)
                  </div>
                )}
              </div>
              <Button disabled={selectionSummary.quality === 0}>
                Lock Selections
              </Button>
            </div>
          </div>
        </div>

        <div className={cn('space-y-4 pt-4', configTab !== 'pi' && 'hidden')}>
          <PIConfigurationTab
            piWeight={currentWeights.pi}
            useModeledPi={useModeledPi}
            modeledPiPercent={modeledScores.piPercent}
            onUseModeledChange={(useModeled) => updateUseModeled('pi', useModeled)}
            onModeledPercentChange={(percent) => updateModeledScore('piPercent', percent)}
          />
        </div>

        <div className={cn('space-y-4 pt-4', configTab !== 'cost' && 'hidden')}>
          <CostConfigurationTab
            costWeight={currentWeights.cost}
            useModeledCost={useModeledCost}
            modeledCostPercent={modeledScores.costPercent}
            onUseModeledChange={(useModeled) => updateUseModeled('cost', useModeled)}
            onModeledPercentChange={(percent) => updateModeledScore('costPercent', percent)}
          />
        </div>

        <div className={cn('space-y-4 pt-4', configTab !== 'ia' && 'hidden')}>
          <IAConfigurationTab
            iaWeight={currentWeights.ia}
            useModeledIa={useModeledIa}
            modeledIaPercent={modeledScores.iaPercent}
            practiceInfo={practiceInfo}
            iaRequirements={iaRequirements}
            iaSelections={iaSelections}
            iaSearchQuery={iaSearchQuery}
            iaSubcategory={iaSubcategory}
            filteredIaMeasures={filteredIaMeasures}
            iaMeasures={iaMeasures}
            iaSavingSelection={iaSavingSelection}
            onUseModeledChange={(useModeled) => updateUseModeled('ia', useModeled)}
            onModeledPercentChange={(percent) => updateModeledScore('iaPercent', percent)}
            onSearchQueryChange={setIaSearchQuery}
            onSubcategoryChange={setIaSubcategory}
            onToggleSelection={toggleIASelection}
          />
        </div>

        <div className={cn('space-y-4', configTab !== 'mvps' && 'hidden')}>
          <ExploreMvpsTab
            practiceId={selectedPracticeId}
            programYear={programYear}
            onGoToQppReports={() => setConfigTab('qpp-reports')}
          />
        </div>
      </div>
    </div>
  );
}